/**
 * Glass Morphism Style Utilities
 * Reusable glass effect styles for modern UI components
 */

/**
 * Base glass morphism effect with white background
 * Perfect for labels, badges, and info boxes on dark/video backgrounds
 *
 * @param {Object} options - Customization options
 * @param {string} options.padding - Custom padding (default: '0.8rem 1.5rem')
 * @param {string} options.borderRadius - Custom border radius (default: '8px')
 * @param {number} options.backgroundOpacity - Background opacity 0-1 (default: 0.12)
 * @param {number} options.borderOpacity - Border opacity 0-1 (default: 0.25)
 * @param {number} options.blurStrength - Backdrop blur in px (default: 10)
 * @returns {Object} MUI sx prop object
 */
const glassEffect = (options = {}) => {
  const {
    padding = { xs: '0.8rem 1.5rem', md: '1rem 2rem' },
    borderRadius = '8px',
    backgroundOpacity = 0.12,
    borderOpacity = 0.25,
    blurStrength = 10
  } = options;

  return {
    display: 'inline-block',
    padding,
    background: `rgba(255, 255, 255, ${backgroundOpacity})`,
    backdropFilter: `blur(${blurStrength}px)`,
    borderRadius,
    border: `1px solid rgba(255, 255, 255, ${borderOpacity})`,
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
  };
};

/**
 * Glass morphism effect with gradient background
 * Perfect for highlighted elements, CTAs, or featured content
 *
 * @param {Object} options - Customization options
 * @param {string} options.color - Base color for gradient (default: '#649fef' - PRIMARY_BLUE)
 * @param {string} options.padding - Custom padding (default: '0.6rem 1.2rem')
 * @param {string} options.borderRadius - Custom border radius (default: '6px')
 * @param {number} options.blurStrength - Backdrop blur in px (default: 8)
 * @returns {Object} MUI sx prop object
 */
const glassEffectGradient = (options = {}) => {
  const {
    color = '#649fef',
    padding = { xs: '0.6rem 1.2rem', md: '0.7rem 1.5rem' },
    borderRadius = '6px',
    blurStrength = 8
  } = options;

  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding,
    background: `linear-gradient(135deg, ${color}22, ${color}44)`,
    backdropFilter: `blur(${blurStrength}px)`,
    borderRadius,
    border: `1px solid ${color}66`,
    boxShadow: `0 4px 20px ${color}33`
  };
};

/**
 * Glass morphism effect with dark background
 * Perfect for content on light backgrounds
 *
 * @param {Object} options - Customization options
 * @param {string} options.padding - Custom padding (default: '0.8rem 1.5rem')
 * @param {string} options.borderRadius - Custom border radius (default: '8px')
 * @param {number} options.backgroundOpacity - Background opacity 0-1 (default: 0.8)
 * @param {number} options.borderOpacity - Border opacity 0-1 (default: 0.2)
 * @param {number} options.blurStrength - Backdrop blur in px (default: 10)
 * @returns {Object} MUI sx prop object
 */
const glassEffectDark = (options = {}) => {
  const {
    padding = { xs: '0.8rem 1.5rem', md: '1rem 2rem' },
    borderRadius = '8px',
    backgroundOpacity = 0.8,
    borderOpacity = 0.2,
    blurStrength = 10
  } = options;

  return {
    display: 'inline-block',
    padding,
    background: `rgba(0, 0, 0, ${backgroundOpacity})`,
    backdropFilter: `blur(${blurStrength}px)`,
    borderRadius,
    border: `1px solid rgba(255, 255, 255, ${borderOpacity})`,
    boxShadow: '0 4px 15px rgba(0,0,0,0.3)'
  };
};

/**
 * Glowing dot indicator
 * Perfect companion for glass effect badges
 *
 * @param {Object} options - Customization options
 * @param {string} options.color - Dot color (default: '#649fef' - PRIMARY_BLUE)
 * @param {string} options.size - Dot size (default: '6px')
 * @returns {Object} MUI sx prop object
 */
const glowingDot = (options = {}) => {
  const { color = '#649fef', size = '6px' } = options;

  return {
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: color,
    boxShadow: `0 0 8px ${color}`
  };
};

// Pre-configured glass styles for common use cases
const GLASS_PRESETS = {
  // Light glass on dark backgrounds
  lightOnDark: glassEffect(),

  // Light glass with more transparency
  lightTransparent: glassEffect({
    backgroundOpacity: 0.08,
    borderOpacity: 0.15
  }),

  // Light glass with strong blur
  lightStrong: glassEffect({
    backgroundOpacity: 0.18,
    borderOpacity: 0.35,
    blurStrength: 15
  }),

  // Gradient glass with primary blue
  gradientBlue: glassEffectGradient(),

  // Gradient glass with custom color (can be overridden)
  gradientCustom: color => glassEffectGradient({ color }),

  // Dark glass on light backgrounds
  darkOnLight: glassEffectDark(),

  // Compact glass for smaller elements
  compact: glassEffect({
    padding: { xs: '0.5rem 1rem', md: '0.6rem 1.2rem' },
    borderRadius: '6px'
  }),

  // Large glass for prominent elements
  large: glassEffect({
    padding: { xs: '1.2rem 2rem', md: '1.5rem 2.5rem' },
    borderRadius: '12px',
    blurStrength: 15
  })
};

// Export all functions and presets
export {
  glassEffect,
  glassEffectGradient,
  glassEffectDark,
  glowingDot,
  GLASS_PRESETS
};

// Default export object for backwards compatibility
const glassMorphismDefaults = {
  glassEffect,
  glassEffectGradient,
  glassEffectDark,
  glowingDot,
  GLASS_PRESETS
};

export default glassMorphismDefaults;
